import { z } from 'zod/v4';
import { PerformedBySchema } from '../../performed-by/index.js';
export var FixedDistributionAccount;
(function (FixedDistributionAccount) {
    FixedDistributionAccount["Debtor"] = "DEBTOR";
    FixedDistributionAccount["Creditor"] = "CREDITOR";
    FixedDistributionAccount["Both"] = "BOTH";
})(FixedDistributionAccount || (FixedDistributionAccount = {}));
export var FixedDistributionDateBehavior;
(function (FixedDistributionDateBehavior) {
    FixedDistributionDateBehavior["FromOriginal"] = "FROM_ORIGINAL";
    FixedDistributionDateBehavior["FromApplied"] = "FROM_APPLIED";
})(FixedDistributionDateBehavior || (FixedDistributionDateBehavior = {}));
export const FixedDistributionSchema = z.object({
    id: z.uuid(),
    tenantId: z.uuid(),
    fromCaseVoucherId: z.uuid(),
    toCaseVoucherId: z.uuid(),
    amount: z.number(),
    account: z.enum(FixedDistributionAccount),
    dateBehavior: z.enum(FixedDistributionDateBehavior),
    associationId: z.uuid().nullable(),
    // Written by engine v2's reconcile, which may move it; any other is manual
    derived: z.boolean(),
    migratedId: z.string().nullable(),
    createdAt: z.string(),
    createdBy: PerformedBySchema.nullable(),
    updatedAt: z.string().nullable(),
    updatedBy: PerformedBySchema.nullable(),
});
export const CreateFixedDistributionSchema = z.object({
    toCaseVoucherId: z.uuid(),
    amount: z.number().int().positive(),
    account: z.enum(FixedDistributionAccount),
    dateBehavior: z.enum(FixedDistributionDateBehavior),
});
export const UpdateFixedDistributionSchema = z.object({
    amount: z.number().int().positive().optional(),
    dateBehavior: z.enum(FixedDistributionDateBehavior).optional(),
});
export const BatchFixedDistributionOperationSchema = z.discriminatedUnion('op', [
    z.object({
        op: z.literal('create'),
        toCaseVoucherId: z.uuid(),
        amount: z.number().int().positive(),
        account: z.enum(FixedDistributionAccount),
        dateBehavior: z.enum(FixedDistributionDateBehavior),
    }),
    z.object({
        op: z.literal('update'),
        id: z.uuid(),
        amount: z.number().int().positive(),
    }),
    z.object({
        op: z.literal('delete'),
        id: z.uuid(),
    }),
]);
export const BatchFixedDistributionSchema = z.object({
    operations: z.array(BatchFixedDistributionOperationSchema).max(100),
});
// The debtor's account steers the economy, the creditor's billing.
export const appliesTo = (perspective) => (fd) => fd.account === perspective || fd.account === FixedDistributionAccount.Both;
export const sumByPerspective = (fds, perspective) => fds.filter(appliesTo(perspective)).reduce((sum, fd) => sum + fd.amount, 0);
export const maxSumAcrossPerspectives = (fds) => Math.max(0, sumByPerspective(fds, FixedDistributionAccount.Debtor), sumByPerspective(fds, FixedDistributionAccount.Creditor));
export const getVoucherCapacityUsage = (distributions, caseVoucherId, perspectives = [
    FixedDistributionAccount.Debtor,
    FixedDistributionAccount.Creditor,
]) => {
    const outgoing = distributions.filter((fd) => fd.fromCaseVoucherId === caseVoucherId);
    const incoming = distributions.filter((fd) => fd.toCaseVoucherId === caseVoucherId);
    return {
        outgoingUsed: Math.max(0, ...perspectives.map((p) => sumByPerspective(outgoing, p))),
        appliedUsed: Math.max(0, ...perspectives.map((p) => sumByPerspective(incoming, p) + sumByPerspective(outgoing, p))),
    };
};
export const getRemainingVoucherCapacity = (distributions, voucher) => Math.max(0, Math.abs(voucher.amount) -
    getVoucherCapacityUsage(distributions, voucher.caseVoucherId).appliedUsed);
export const getRemainingCapacity = (distributions, fromVoucher, toVoucher, account, options) => {
    const filtered = options?.excludeId
        ? distributions.filter((fd) => fd.id !== options.excludeId)
        : distributions;
    const perspectivesToCheck = account === FixedDistributionAccount.Both
        ? [FixedDistributionAccount.Debtor, FixedDistributionAccount.Creditor]
        : [account];
    const sourceUsed = getVoucherCapacityUsage(filtered, fromVoucher.caseVoucherId, perspectivesToCheck).outgoingUsed;
    const sourceAvailable = Math.abs(fromVoucher.amount) - sourceUsed;
    const targetUsed = getVoucherCapacityUsage(filtered, toVoucher.caseVoucherId, perspectivesToCheck).appliedUsed;
    const targetAvailable = Math.abs(toVoucher.amount) - targetUsed;
    return { sourceAvailable, targetAvailable };
};
//# sourceMappingURL=index.js.map